package dev.lingvo.site.components.editor

import dev.lingvo.shared.PluralCategory
import dev.lingvo.shared.ProposalDto
import dev.lingvo.site.i18n.tr

internal const val EDITOR_ASSIST_RESULT_IDENTITY_ATTRIBUTE = "data-editor-assist-result-identity"

/** One rank shared by the rendered Assist stream and its Alt+number shortcuts. */
internal sealed interface EditorAssistResult {
    val shortcutNumber: Int

    data class Proposal(
        val proposal: ProposalDto,
        override val shortcutNumber: Int,
    ) : EditorAssistResult

    data class Suggestion(
        val suggestion: EditorSuggestion,
        override val shortcutNumber: Int,
    ) : EditorAssistResult
}

internal fun rankedAssistResults(
    proposal: ProposalDto?,
    suggestions: List<EditorSuggestion>,
): List<EditorAssistResult> = buildList {
    proposal?.let { add(EditorAssistResult.Proposal(it, shortcutNumber = 1)) }
    suggestions.forEach { suggestion ->
        add(
            EditorAssistResult.Suggestion(
                suggestion = suggestion,
                shortcutNumber = size + 1,
            ),
        )
    }
}.take(9)

/**
 * A result shortcut is meaningful only while that exact Assist surface is fully present.
 *
 * Unsafe suggestions and stale proposals never apply from the chord. They move focus to the
 * already-visible explicit action instead, so the warning remains in the decision path.
 */
internal sealed interface EditorAssistShortcutResolution {
    data class Apply(val result: EditorAssistResult) : EditorAssistShortcutResolution

    data class Review(
        val actionId: String,
        val actionIdentity: String,
    ) : EditorAssistShortcutResolution

    data object Unavailable : EditorAssistShortcutResolution
}

internal fun resolveAssistShortcutResult(
    workspace: EditorWorkspaceState,
    ranked: List<EditorAssistResult>,
    index: Int,
): EditorAssistShortcutResolution {
    if (workspace.utility != EditorUtilitySurface.ASSIST) {
        return EditorAssistShortcutResolution.Unavailable
    }
    if (workspace.resolvedUtilityPresence().phase != EditorUtilityPresencePhase.PRESENT) {
        return EditorAssistShortcutResolution.Unavailable
    }
    val result = ranked.getOrNull(index) ?: return EditorAssistShortcutResolution.Unavailable
    return when (result) {
        is EditorAssistResult.Proposal -> if (result.proposal.stale) {
            EditorAssistShortcutResolution.Review(
                actionId = result.visibleActionId(),
                actionIdentity = result.actionIdentity(),
            )
        } else {
            EditorAssistShortcutResolution.Apply(result)
        }

        is EditorAssistResult.Suggestion -> if (result.suggestion.warning != null) {
            EditorAssistShortcutResolution.Review(
                actionId = result.visibleActionId(),
                actionIdentity = result.actionIdentity(),
            )
        } else {
            EditorAssistShortcutResolution.Apply(result)
        }
    }
}

internal fun EditorAssistResult.visibleActionId(): String = when (this) {
    is EditorAssistResult.Proposal -> "editor-assist-result-$shortcutNumber-accept"
    is EditorAssistResult.Suggestion -> "editor-assist-result-$shortcutNumber-insert"
}

/**
 * Collision-free identity for the exact candidate snapshot represented by a visible action.
 *
 * Stable ids alone are insufficient: the same proposal can become stale, and an async suggestion
 * can retain its id while its warning or candidate content changes. Length-prefixed parts keep
 * arbitrary translated text unambiguous without relying on a hash.
 */
internal fun EditorAssistResult.actionIdentity(): String = when (this) {
    is EditorAssistResult.Proposal -> canonicalActionIdentity(
        "proposal",
        proposal.id,
        proposal.keyId,
        proposal.locale,
        proposal.stale.toString(),
        proposal.value,
        proposal.pluralForms.canonicalPluralForms(),
    )

    is EditorAssistResult.Suggestion -> when (val item = suggestion) {
        is EditorSuggestion.Machine -> canonicalActionIdentity(
            "machine",
            item.id,
            item.provider.id,
            item.accepted.toString(),
            item.warning,
            item.value,
            item.pluralForms.canonicalPluralForms(),
        )

        is EditorSuggestion.TranslationMemory -> canonicalActionIdentity(
            "translation-memory",
            item.id,
            item.warning,
            item.value,
            item.pluralForms.canonicalPluralForms(),
            item.sourceKeyName,
            item.similarity.toString(),
            item.matchedSource,
        )
    }
}

private fun Map<PluralCategory, String>?.canonicalPluralForms(): String? = this
    ?.entries
    ?.sortedBy { it.key.name }
    ?.joinToString(separator = "") { (category, value) ->
        canonicalActionIdentity(category.name, value)
    }

private fun canonicalActionIdentity(vararg parts: String?): String = buildString {
    parts.forEach { part ->
        if (part == null) {
            append("-1:")
        } else {
            append(part.length).append(':').append(part)
        }
    }
}

/** Placeholder comparison is the only validation performed before an Assist result is shown. */
internal fun assistSuggestionStatus(suggestion: EditorSuggestion): String =
    if (suggestion.warning == null) {
        tr("editor.editor_assist_ranking.placeholders_match", "Placeholders match")
    } else {
        tr("editor.editor_assist_ranking.review_required", "Review required")
    }
